import { useState, useEffect } from 'react';
import API_CONFIG from '../config/api';

export interface DriveVideo {
  id: string;
  name: string;
  size: string;
  createdTime: string;
  directLink: string;
  webViewLink: string;
  thumbnail: string | null;
  preview: string | null;
}

export interface DriveStatus {
  success: boolean;
  message: string;
  user?: {
    displayName?: string;
    emailAddress?: string;
  };
}

export const useGoogleDriveStatus = () => {
  const [status, setStatus] = useState<DriveStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/drive-status`);
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка проверки статуса');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  return { status, loading, error, refetch: checkStatus };
};

export const useGoogleDriveVideos = () => {
  const [videos, setVideos] = useState<DriveVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVideos = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/drive-videos`);
      const data = await response.json();
      
      if (data.success) {
        setVideos(data.videos);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки видео');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  return { videos, loading, error, refetch: fetchVideos };
};

export const useAssignVideoToLesson = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assignVideo = async (lessonId: number, videoId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/lesson/${lessonId}/drive-ids`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          driveVideoId: videoId
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message);
      }

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка назначения видео';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return { assignVideo, loading, error };
};